/* eslint-disable simple-import-sort/imports */
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Suspense, useRef} from 'react';
import Modal from 'react-modal';
import cn from 'classnames';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import {
	useClientServerEffect,
	useCmsContent,
	useHeaderRotationalBanner,
	useNavigationLinkImages,
	useRequestClass,
	useSetUI,
	useUI,
} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import {useSyncSSRStyle} from 'coreHooks/useSSRStyle';
import NonCriticalBoundary from 'coreComponents/NonCriticalBoundary';

import Header from 'brandComponents/Header';
import {IShellHeaderProps} from 'brandComponents/Header/IHeader';
import Footer from 'brandComponents/Footer';
import {parseTexts} from 'brandComponents/RotationalBanner/helpers';
import type {TRotationalBannerData} from 'brandComponents/RotationalBanner/IRotationalBanner';

import type {IContentfulHeaderCmsData} from 'brand/app/contentful/types/IContentfulHeader';

import FontsPreload from '../FontsPreload';
import ResourceHints from '../ResourceHints';
import livePreviewStyles from './livePreview.module.scss';
import {IAppProps} from './IApp';
import {useServerEffect} from 'coreHooks/useClientServerEffect';

Modal.setAppElement('#react-target');

const ShellHeader = (
	{
		headerData,
		rotationalBannerData,
		livePreviewEntryId,
	}: IShellHeaderProps) => {
	return (
		<Header
			headerData={headerData}
			rotationalBannerData={rotationalBannerData}
			livePreviewEntryId={livePreviewEntryId}
		/>
	);
};

const App = ({children}: IAppProps) => {
	const appMainRef = useRef(null);
	const {setRef} = useGeneralProvider();
	const mainContainerCssClasses = useUI('mainContainerCssClasses');
	const requestClass = useRequestClass();
	const categoryActions = useCategoryActions();
	const setUI = useSetUI();
	const isFooterVisible = useUI('footer')?.show;

	const {data: rawCmsData} = useCmsContent<IContentfulHeaderCmsData>({
		contentType: 'header',
		collectionKey: 'headerCollection',
		refetchOnMount: 'always'
	});

	const processedHeaderData = useNavigationLinkImages(rawCmsData);

	const {rotationalBannerData, livePreviewEntryId} = useHeaderRotationalBanner<TRotationalBannerData>(
		processedHeaderData,
		parseTexts,
		categoryActions,
	);

	useServerEffect(() => {
		if (!rotationalBannerData) {
			setUI.setRotationalBanner(false);
		}
	}, []);

	useClientServerEffect(() => {
		setRef('appMain', appMainRef);
	}, []);

	useSyncSSRStyle();

	const rotationalBanner = useUI('rotationalBanner');

	return (
		<div
			id="app-main"
			className={cn(
				{
					'rotational-banner__hidden': !rotationalBanner,
					[livePreviewStyles['isLivePreview']]: requestClass.isLivePreview,
				},
				mainContainerCssClasses,
			)}
			ref={appMainRef}
		>
			<div className={cn('app-container')}>
				<Suspense fallback={null}>
					<ShellHeader
						headerData={processedHeaderData}
						rotationalBannerData={rotationalBannerData}
						livePreviewEntryId={livePreviewEntryId}
					/>
				</Suspense>
				<FontsPreload />
				<ResourceHints />
				<main>{children}</main>
				{isFooterVisible ? (
					<NonCriticalBoundary fallback={<footer className="footer" />}>
						<Footer />
					</NonCriticalBoundary>
				) : null}
			</div>
		</div>
	);
};

export default App;
